import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialPointOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"
import type {MappingVectorType} from "#material/nodes/vector/mapping/types"
import {mappingVectorTypeSchema} from "#material/nodes/vector/mapping/types"

export interface MappingSockets extends MaterialNodeSockets {
    inputs: {
        location: "point" // found in inputs: vector; found in settings: vector; inferred from data: float3,null3,nullableFloat3
        rotation: "point" // found in inputs: -; found in settings: euler,vector; inferred from data: float3,nullableFloat3
        scale: "point" // found in inputs: point,float; found in settings: vector; inferred from data: float3
        vector: "point" // found in inputs: point,color; found in settings: vector; inferred from data: float3
    }
    settings: {
        location: "point" // found in inputs: vector; found in settings: vector; inferred from data: float3,null3,nullableFloat3
        rotation: "point" // found in inputs: -; found in settings: euler,vector; inferred from data: float3,nullableFloat3
        scale: "point" // found in inputs: point,float; found in settings: vector; inferred from data: float3
        vector: "point" // found in inputs: point,color; found in settings: vector; inferred from data: float3
    }
    settingsExtensions: {
        vectorType?: MappingVectorType // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        vector: "point"
    }
}

// a vector or a normal has no origin to translate, so Blender drops the location for those
const HAS_LOCATION = {setting: "vectorType", oneOf: [undefined, "POINT", "TEXTURE"]} as const

export const mappingDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeMapping,
    },
    nodeClass: "Mapping",
    ui: {
        label: "Mapping",
        color: "#6e5eb2",
        items: [
            {name: "vector", valueType: "point", label: "Vector", output: true},
            {name: "vector", valueType: "point", label: "Vector", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            {name: "location", valueType: "point", label: "Location", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}, visibleWhen: HAS_LOCATION},
            {name: "rotation", valueType: "point", label: "Rotation", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            {
                name: "scale",
                valueType: "point",
                label: "Scale",
                defaultValue: {x: 1, y: 1, z: 1},
                input: true,
                setting: true,
            },
            {
                name: "vectorType",
                label: "Vector Type",
                valueType: "string",
                defaultValue: "TEXTURE",
                options: [
                    {label: "Point", value: "POINT"},
                    {label: "Texture", value: "TEXTURE"},
                    {label: "Vector", value: "VECTOR"},
                    {label: "Normal", value: "NORMAL"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({vector: materialPointOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                location: optionalMaterialInput,
                rotation: optionalMaterialInput,
                scale: optionalMaterialInput,
                vector: optionalMaterialInput,
            }),
            settings: z.strictObject({
                location: z.optional(vec3),
                rotation: z.optional(vec3),
                scale: z.optional(vec3),
                vector: z.optional(vec3),
                vectorType: z.optional(mappingVectorTypeSchema),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<MappingSockets>
